import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon, { type IconName } from '../components/Icon'
import { CONDITIONS } from '../../shared/market/model'
import { conditionText, getLocale } from '../lib/i18n'

/**
 * The four conditions as an icon and a colour, so an ad reads at a glance: green like new, blue good, amber used, red needs repair.
 * The same look is the picker in the editor and the badge on a card and on the ad's own page.
 */
type Condition = typeof CONDITIONS[number]
const L = (en: string, es: string) => (getLocale() === 'en' ? en : es)
export const CONDITION_LOOK: Record<Condition, { icon: IconName; color: string; ink: string }> = {
  'like-new': { icon: 'sparkles', color: '#1f9d55', ink: '#fff' },
  good: { icon: 'check', color: '#1c7ed6', ink: '#fff' },
  used: { icon: 'clock', color: '#f0a500', ink: '#2b1d00' },
  repair: { icon: 'wrench', color: '#c0392b', ink: '#fff' },
}
const hint = (c: Condition) => ({
  'like-new': L('Barely or never used. Nothing wrong with it.', 'Casi sin uso o nuevo. Sin ningún defecto.'),
  good: L('Works well, with light signs of use.', 'Funciona bien, con señales leves de uso.'),
  used: L('Clearly used, and still does its job.', 'Se nota que se usó, y todavía cumple.'),
  repair: L('Needs fixing, or is sold for parts.', 'Necesita arreglo, o se vende para piezas.'),
}[c])
const known = (v: string): v is Condition => (CONDITIONS as readonly string[]).includes(v)

/** A small coloured pill with the condition's icon and name. */
export function ConditionBadge({ condition }: { condition: string }) {
  if (!known(condition)) return <>{conditionText(condition)}</>
  const look = CONDITION_LOOK[condition]
  return <span className="cond-badge" data-condition={condition} style={{ background: look.color, color: look.ink }} title={hint(condition)}><Icon name={look.icon} size={13}/>{conditionText(condition)}</span>
}

/** Four icons in a centred row; the chosen one fills with its colour and its meaning shows underneath. */
export function ConditionPicker({ value, onChange, invalid, label }: { value: string; onChange: (v: string) => void; invalid?: boolean; label: string }) {
  return <div className="cond-picker">
    <span className="field-label" id="condition-label">{label}</span>
    <div role="radiogroup" aria-labelledby="condition-label" aria-invalid={invalid || undefined} className="cond-row">
      {CONDITIONS.map(c => {
        const look = CONDITION_LOOK[c], on = value === c
        return <button key={c} type="button" role="radio" aria-checked={on} aria-label={conditionText(c)} title={`${conditionText(c)}: ${hint(c)}`} className={on ? 'cond-dot on' : 'cond-dot'}
          style={{ ['--cc' as string]: look.color, ['--ci' as string]: look.ink }} onClick={() => onChange(c)}><Icon name={look.icon} size={26}/></button>
      })}
    </div>
    <p className="cond-caption" aria-live="polite">{known(value) ? <><strong>{conditionText(value)}</strong> {hint(value)}</> : L('Tap one to say what shape it is in.', 'Toca uno para decir en qué estado está.')}</p>
  </div>
}

/**
 * "Offers welcome": the seller will talk about the price. Shown on a sale that is open to offers.
 * On a card there is no room for words, so `compact` shows only the icon; hovering, focusing or touching it opens a short blurb.
 */
export function OffersBadge({ item, compact }: { item: { mode: string; acceptsOffers?: boolean }; compact?: boolean }) {
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!tip) return
    const close = () => setTip(null)
    window.addEventListener('scroll', close, true); window.addEventListener('pointerdown', close)
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('pointerdown', close) }
  }, [tip])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  if (!item.acceptsOffers || item.mode !== 'sale') return null
  const title = L('Offers welcome', 'Acepto ofertas')
  const blurb = L('The seller is open to offers on the price. Tap Make an offer on the ad to suggest yours.', 'El vendedor acepta ofertas sobre el precio. Toca Hacer una oferta en el anuncio para proponer la tuya.')
  if (!compact) return <span className="offer-badge" title={blurb}><Icon name="handshake" size={13}/>{title}</span>
  const show = (el: HTMLElement) => { const r = el.getBoundingClientRect(); window.clearTimeout(timer.current); setTip({ x: r.left + r.width / 2, y: r.top }); timer.current = window.setTimeout(() => setTip(null), 4000) }
  return <>
    <button type="button" className="offer-badge offer-icon" aria-label={`${title}. ${blurb}`} aria-expanded={Boolean(tip)}
      onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); if (tip) setTip(null); else show(e.currentTarget) }}
      onMouseEnter={e => show(e.currentTarget)} onMouseLeave={() => setTip(null)} onFocus={e => show(e.currentTarget)} onBlur={() => setTip(null)}><Icon name="handshake" size={15}/></button>
    {tip && createPortal(<div role="tooltip" className="offer-tip" style={{ left: Math.min(Math.max(tip.x, 130), window.innerWidth - 130), top: tip.y - 8 }}><strong>{title}</strong>{blurb}</div>, document.body)}
  </>
}
